import React, { useState, useEffect, useRef, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import LinImage from '../../../../assets/images/Lin.png'
import RonImage from '../../../../assets/images/Ron.png'
import MentorSolinaImage from '../../../../assets/images/MentorSolina.svg'

const MentorEnum = {
    Abe: 0,
    Lin: 1,
    Bell: 2,
    Ron: 3,
    Solina: 4,
    NoMentor: 100,
}

function getMentorImage(mentorType) {
    switch (mentorType) {
        case MentorEnum.Ron:
            return RonImage
        case MentorEnum.Solina:
            return MentorSolinaImage
        case MentorEnum.Lin:
        case MentorEnum.Abe:
        case MentorEnum.Bell:
        default:
            return LinImage
    }
}

const MENTOR_ASPECT_RATIO = 160 / 320

const useStyles = makeStyles((theme) => ({
    sceneWrapper: {
        width: '100%',
        maxWidth: 800,
        margin: '0 auto',
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        '@media (max-width: 599px)': {
            borderRadius: 0,
        },
    },
    backgroundImage: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },
    mentorImg: {
        position: 'absolute',
        objectFit: 'contain',
        objectPosition: 'bottom',
        pointerEvents: 'none',
        willChange: 'left, bottom, height',
    },
    contentOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 2,
    },
}))

/**
 * Renders a mentor character within the scene, animating from the
 * previous node's position to the current position.
 */
const AnimatedMentor = ({ mentor, prevMentor, animationDuration }) => {
    const classes = useStyles()
    const [isAnimating, setIsAnimating] = useState(false)
    const frameRef = useRef(null)

    const pos = mentor.screenObjectDto?.normalizedPosition || { x: 0.5, y: 0 }
    const size = mentor.screenObjectDto?.normalizedSize || { x: 0.2, y: 0.4 }

    const prevPos = prevMentor?.screenObjectDto?.normalizedPosition
    const prevSize = prevMentor?.screenObjectDto?.normalizedSize

    const hasPrevious = !!prevPos && !!prevSize

    useEffect(() => {
        if (!hasPrevious) {
            setIsAnimating(true)
            return
        }
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = requestAnimationFrame(() => {
                setIsAnimating(true)
            })
        })
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current)
        }
    }, [hasPrevious])

    const startLeft = hasPrevious ? prevPos.x * 100 : pos.x * 100
    const startBottom = hasPrevious ? prevPos.y * 100 : pos.y * 100
    const startHeight = hasPrevious ? prevSize.y * 100 : size.y * 100

    const targetLeft = pos.x * 100
    const targetBottom = pos.y * 100
    const targetHeight = size.y * 100

    const currentLeft = isAnimating ? targetLeft : startLeft
    const currentBottom = isAnimating ? targetBottom : startBottom
    const currentHeight = isAnimating ? targetHeight : startHeight
    const currentWidth = currentHeight * MENTOR_ASPECT_RATIO

    const duration = hasPrevious ? animationDuration : 0

    const mentorImage = getMentorImage(mentor.mentor)

    if (mentor.mentor === MentorEnum.NoMentor) return null

    return (
        <img
            src={mentorImage}
            alt=""
            className={classes.mentorImg}
            style={{
                left: `${currentLeft}%`,
                bottom: `${currentBottom}%`,
                height: `${currentHeight}%`,
                width: `${currentWidth}%`,
                transition: duration > 0
                    ? `left ${duration}s ease-in-out, bottom ${duration}s ease-in-out, height ${duration}s ease-in-out, width ${duration}s ease-in-out`
                    : 'none',
            }}
        />
    )
}

/**
 * SceneBackground — renders the background image as a prominent section
 * with mentors positioned and animated within it.
 *
 * Props:
 *   node         – current ScenarioNodeDto
 *   previousNode – previous ScenarioNodeDto (for mentor animation start positions)
 *   imageMap     – { [url]: blobUrl }
 *   duration     – animation duration in seconds (typically nodeLengthSeconds)
 *   children     – optional overlay content rendered at the bottom of the scene
 */
const SceneBackground = ({ node, previousNode, imageMap, duration, children }) => {
    const classes = useStyles()

    const bgUrl = node.backgroundImageUrl
        ? imageMap[node.backgroundImageUrl] || null
        : null

    const mentors = useMemo(
        () => (node.scenarioMentors || []).filter((m) => m.mentor !== MentorEnum.NoMentor),
        [node.scenarioMentors]
    )

    const prevMentors = useMemo(
        () => previousNode?.scenarioMentors || [],
        [previousNode?.scenarioMentors]
    )

    const findPreviousMentor = (currentMentor) => {
        return prevMentors.find((pm) => pm.mentor === currentMentor.mentor) || null
    }

    const animDuration = duration || 2

    if (!bgUrl && mentors.length === 0) return null

    return (
        <div className={classes.sceneWrapper}>
            {bgUrl && (
                <img
                    src={bgUrl}
                    alt=""
                    className={classes.backgroundImage}
                />
            )}

            {!bgUrl && (
                <div style={{ width: '100%', paddingBottom: '56.25%' }} />
            )}

            {mentors.map((mentor, i) => (
                <AnimatedMentor
                    key={`${mentor.mentor}-${i}`}
                    mentor={mentor}
                    prevMentor={findPreviousMentor(mentor)}
                    animationDuration={animDuration}
                />
            ))}

            {children && (
                <div className={classes.contentOverlay}>
                    {children}
                </div>
            )}
        </div>
    )
}

export default SceneBackground
